import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ShoppingBag } from 'lucide-react';
import PopUpMini from './PopUpMini';
import PopUpMax from './PopUpMax';
import { DrawablyButton } from 'drawably/react';
import { getSiteContent } from '#api';
import defaultSiteContent from '../../utils/siteContentDefaults';
import PropTypes from 'prop-types';

const SPECIAL_POPUP_DISMISSAL_KEY = 'SPECIAL_POPUP_DISMISSAL';

const getCurrentDate = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

const PopUpContext = React.createContext();

const usePopUp = () => {
    return useContext(PopUpContext);
};

const PopUpProvider = ({ children }) => {
    const pageContentRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const [popUpsList, setPopUpsList] = useState([]);
    const [maxPopUp, setMaxPopUp] = useState(null);
    const isAdminRoute = location.pathname.startsWith('/admin');

    const newPopUp = (type = '', title, content, highlightedElement = null, duration = 4) => {
        setPopUpsList((prevList) => [
            ...prevList,
            {
                id: uuidv4(),
                type: type,
                title: title,
                content: content,
                duration: duration,
            },
        ]);
        const highlightedElements = Array.isArray(highlightedElement)
            ? highlightedElement.filter((element) => element instanceof HTMLElement)
            : highlightedElement instanceof HTMLElement
              ? [highlightedElement]
              : [];

        const firstElement = highlightedElements[0];
        if (!firstElement) return;

        try {
            firstElement.focus();
        } catch (error) {
            console.error('Unable to focus highlighted field:', error);
        }
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        highlightedElements.forEach((element) => {
            element.style.animation = 'highlightField .5s 4';
            setTimeout(() => element.style.removeProperty('animation'), 2000);
        });
    };

    const open = useCallback((title, content, onClose) => {
        setMaxPopUp({ title, content, onClose });
    }, []);

    const openShop = () => {
        setMaxPopUp({
            title: 'Kup Online!',
            content: (
                <iframe
                    title="Sklep online"
                    id="dl-widget"
                    src="https://widget.droplabs.pl/index.html?facilityId=1292&onlineGroupId=13542"
                    allow="payment"
                />
            ),
        });
    };

    const close = useCallback((onClose) => {
        onClose?.();
        setMaxPopUp(null);
    }, []);

    useEffect(() => {
        if (isAdminRoute) return undefined;

        let isActive = true;
        const loadSpecialPopup = async () => {
            const response = await getSiteContent();
            if (!isActive) return;
            if (response.error) {
                console.error('Error loading special popup:', response.error);
            }

            const popup = response.data?.popup || defaultSiteContent.popup;
            if (!popup.enabled || !popup.image) return;

            const popupSignature = JSON.stringify({
                enabled: popup.enabled,
                title: popup.title || '',
                image: popup.image,
                url: popup.url || '',
            });
            let dismissal;

            try {
                dismissal = JSON.parse(localStorage.getItem(SPECIAL_POPUP_DISMISSAL_KEY));
            } catch (error) {
                console.error('Error reading special popup dismissal:', error);
            }

            if (
                dismissal?.signature === popupSignature &&
                dismissal?.date === getCurrentDate()
            ) {
                return;
            }

            open(
                popup.title || 'Specjalna oferta',
                <div className="anikino__popup-max-special">
                    <img
                        className="anikino__popup-max-special-image"
                        src={`${import.meta.env.VITE_API_EXPRESS}/${popup.image}`}
                        alt={popup.title || 'Popup'}
                    />
                    <button
                        className="anikino__popup-max-special-button"
                        type="button"
                        onClick={() => {
                            close(dismissSpecialPopup);
                            if (popup.url) navigate(popup.url);
                        }}
                    >
                        Przejdź
                    </button>
                </div>,
                dismissSpecialPopup,
            );

            function dismissSpecialPopup() {
                try {
                    localStorage.setItem(
                        SPECIAL_POPUP_DISMISSAL_KEY,
                        JSON.stringify({
                            signature: popupSignature,
                            date: getCurrentDate(),
                        }),
                    );
                } catch (error) {
                    console.error('Error saving special popup dismissal:', error);
                }
            }
        };

        loadSpecialPopup();
        return () => {
            isActive = false;
        };
    }, [isAdminRoute, navigate, open, close]);

    useEffect(() => {
        if (pageContentRef.current) {
            pageContentRef.current.inert = Boolean(maxPopUp);
        }
    }, [maxPopUp]);

    useEffect(() => {
        const firstPopUp = popUpsList[0];
        if (!firstPopUp) return;

        const timeoutId = setTimeout(() => {
            setPopUpsList((prevList) => prevList.slice(1));
        }, 1000 * firstPopUp.duration);

        return () => clearTimeout(timeoutId);
    }, [popUpsList]);

    useEffect(() => {
        const lastShownErrors = new Map();
        const handleApiError = (event) => {
            const { title, message } = event.detail || {};
            if (typeof title !== 'string' || typeof message !== 'string') return;

            const signature = title;
            const now = Date.now();
            if (now - (lastShownErrors.get(signature) || 0) < 4000) return;
            lastShownErrors.set(signature, now);

            setPopUpsList((previous) => [
                ...previous,
                {
                    id: uuidv4(),
                    type: 'error',
                    className: 'api-error',
                    title,
                    content: message,
                    duration: 8,
                },
            ]);
        };

        window.addEventListener('anikino:api-error', handleApiError);
        return () => window.removeEventListener('anikino:api-error', handleApiError);
    }, []);

    const value = {
        newPopUp,
        open,
        close,
    };

    return (
        <PopUpContext.Provider value={value}>
            <div ref={pageContentRef}>{children}</div>
            <div className="anikino__popup-container">
                {popUpsList.map((element) => (
                    <PopUpMini
                        type={element.type}
                        title={element.title}
                        content={element.content}
                        duration={element.duration}
                        className={element.className}
                        key={element.id}
                    />
                ))}
                {!isAdminRoute && (
                    <DrawablyButton
                        className="anikino__popup-shop"
                        type="button"
                        onClick={openShop}
                    >
                        <ShoppingBag size={20} aria-hidden="true" />
                        Kup Online!
                    </DrawablyButton>
                )}
            </div>
            {maxPopUp && (
                <PopUpMax
                    title={maxPopUp.title}
                    content={maxPopUp.content}
                    onClose={() => close(maxPopUp.onClose)}
                />
            )}
        </PopUpContext.Provider>
    );
};

PopUpProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export { usePopUp };
export default PopUpProvider;
